import {
  GridColDef,
  GridRenderCellParams,
  GridFilterModel,
  GridRowId,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useQuery, useQueries } from '@tanstack/react-query';
import {
  doApiCheck,
  getAllDriveLabelsForManagerQuery,
  getDriveLabelLocksQuery,
  UserKeys,
} from '../../../api/users-api';
import { useCallback, useMemo, useState, useEffect } from 'react';
import LabelManagerGridToolbar from './label-manager-grid-toolbar';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import { useUserSettings } from '../../../utils/use-user-settings';
import UserAvatar from '../../../shared/user-avatar';
import { drivelabels_v2 } from '@googleapis/drivelabels';
import { StatCard } from '../../../shared/stat-card';
import useGridColumns from '../../../shared/hooks/use-grid-columns-latest';
import StatusLabel, {
  palette as statusPalette,
} from '../../../shared/status-label';
import CircleIcon from '@mui/icons-material/Circle';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LabelDetailsDrawer from './label-details-drawer';
import ScopesAlert from '../../../shared/scopes-alert';
import { getLabelLifecycleStatus } from './label-lifecycle';
import { useLabelActions } from './use-label-actions';
import LabelActionsMenu from './label-actions-menu';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import AccessTime from '@mui/icons-material/AccessTime';
import AddToDrive from '@mui/icons-material/AddToDrive';
import Check from '@mui/icons-material/Check';
import Label from '@mui/icons-material/Label';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { pagesConfig } from '../../../shared/pages-config';
import { useNavigate } from 'react-router-dom';

type DriveLabel = drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label;

interface LabelWithRank extends DriveLabel {
  rankOrder: number;
  hasBadge: boolean;
}

export default function LabelManagerGrid() {
  const theme = useTheme();
  const {
    data: driveLabelsAccessGranted,
    isLoading: checkingDriveLabelsAccess,
  } = useQuery({
    queryKey: [UserKeys.AdminApiSettings, 'driveLabels'],
    queryFn: () => doApiCheck('driveLabels', null),
    staleTime: 0,
  });

  const {
    data: labels,
    isLoading: labelsLoading,
    isFetching: labelsFetching,
  } = useQuery({
    ...getAllDriveLabelsForManagerQuery(),
    enabled: !!driveLabelsAccessGranted,
  });

  const { viewType, changeLogRoles, features } = useUserSettings();
  const canManageLabels = isFeatureEnabled('driveLabelsQ4', features);
  const navigate = useNavigate();
  const openEditLabel = useCallback(
    (labelId: string) =>
      navigate(
        `/${pagesConfig.editDriveLabel.link.replace(
          ':labelId',
          encodeURIComponent(labelId)
        )}`
      ),
    [navigate]
  );

  const [selectedLabelId, setSelectedLabelId] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);

  const labelActions = useLabelActions({
    changeLogRoles,
    onChanged: (changed, action) => {
      setSelectedRows((rows) =>
        rows.filter((id) => !changed.some((label) => label.id === id))
      );
      if (
        action === 'delete' &&
        changed.some((label) => label.id === selectedLabelId)
      ) {
        setSelectedLabelId(null);
      }
    },
  });
  const {
    requestPublish,
    isPublishing,
    cloneLabel,
    requestDelete,
    requestDisable,
    requestEnable,
    isDeleting,
  } = labelActions;

  const allLabels = useMemo(() => {
    const labelArray = Array.isArray(labels?.labels) ? labels.labels : [];
    return labelArray.map((entry, index) => ({
      ...entry.label,
      rankOrder: index + 1,
      hasBadge: entry.hasBadge,
    }));
  }, [labels?.labels]);

  // The drawer follows the live row, so it shows the new status after a Disable or Enable and
  // closes on its own once its label is gone.
  const selectedLabel = useMemo(
    () => allLabels.find((label) => label.id === selectedLabelId) ?? null,
    [allLabels, selectedLabelId]
  );

  const labelIds = useMemo(
    () => allLabels.map((label) => label.id).filter((id): id is string => !!id),
    [allLabels]
  );

  const { hasActiveLockByLabelId, isLockPendingByLabelId } = useQueries({
    queries: labelIds.map((labelId) => ({
      ...getDriveLabelLocksQuery(labelId),
    })),
    combine: (results) => {
      const lockMap: Record<string, boolean> = {};
      const pendingMap: Record<string, boolean> = {};
      results.forEach((result, index) => {
        const labelId = labelIds[index];
        lockMap[labelId] = (result.data ?? []).some(
          (lock) => lock.state === 'ACTIVE'
        );
        pendingMap[labelId] = result.isPending;
      });
      return {
        hasActiveLockByLabelId: lockMap,
        isLockPendingByLabelId: pendingMap,
      };
    },
  });

  const publishedCount = useMemo(
    () =>
      allLabels.filter(
        (label) =>
          String(label?.lifecycle?.state ?? '')
            .trim()
            .toLowerCase() === 'published'
      ).length,
    [allLabels]
  );

  const draftCount = useMemo(
    () =>
      allLabels.filter(
        (label) =>
          String(label?.lifecycle?.state ?? '')
            .trim()
            .toLowerCase() === 'unpublished_draft'
      ).length,
    [allLabels]
  );

  const columns: GridColDef<LabelWithRank>[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'Label ID',
        minWidth: 120,
        flex: 1,
        hide: true,
      },
      {
        field: 'rankOrder',
        headerName: 'Rank',
        type: 'number',
        width: 80,
        sortable: true,
        valueGetter: (_value, row) => row.rankOrder ?? 0,
      },
      {
        field: 'title',
        headerName: 'Label',
        flex: 1,
        minWidth: 150,
        valueGetter: (_value, row) => row.properties?.title,
        renderCell: (params: GridRenderCellParams<LabelWithRank>) => {
          return (
            <Stack
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              sx={{ height: '100%', width: '100%' }}
            >
              <Typography
                component="div"
                variant="body2"
                noWrap
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.value}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                flexShrink={0}
              >
                {params.row.hasBadge && (
                  <Tooltip title="This is a badged label" arrow>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <LocalOfferOutlinedIcon
                        sx={{ width: 16, height: 16 }}
                        color="action"
                      />
                    </Box>
                  </Tooltip>
                )}
                {isLockPendingByLabelId[params.row.id ?? ''] && (
                  <Skeleton variant="circular" width={16} height={16} />
                )}
                {!isLockPendingByLabelId[params.row.id ?? ''] &&
                  hasActiveLockByLabelId[params.row.id ?? ''] && (
                    <Tooltip
                      title="This label is associated with rule(s) that lock it from changes"
                      arrow
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <LockOutlinedIcon
                          sx={{ width: 16, height: 16 }}
                          color="action"
                        />
                      </Box>
                    </Tooltip>
                  )}
              </Stack>
            </Stack>
          );
        },
      },
      {
        field: 'fieldCount',
        headerName: 'Field Count',
        width: 100,
        type: 'number',
        valueGetter: (_value, row) => row.fields?.length ?? 0,
      },
      {
        field: 'creator',
        headerName: 'Creator',
        flex: 1,
        minWidth: 150,
        valueGetter: (_value, row) => row?.creator?.person,
        renderCell: (params: GridRenderCellParams) => {
          const value = String(params.value ?? '');
          const content = (
            <Typography
              component="div"
              variant="body2"
              noWrap
              sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value}
            </Typography>
          );

          return (
            <Stack
              flexDirection="row"
              alignItems="center"
              gap={2}
              sx={{ height: '100%' }}
            >
              <UserAvatar
                userEmail={params.value}
                sx={{ width: 32, height: 32 }}
              />
              {content}
            </Stack>
          );
        },
      },
      {
        field: 'revisionCreateTime',
        headerName: 'Last Modified',
        width: 180,
        renderCell: (params: GridRenderCellParams) => {
          const rawValue =
            params?.value ?? params?.row?.revisionCreateTime ?? null;
          if (!rawValue) {
            return 'N/A';
          }

          const timestamp =
            typeof rawValue === 'number'
              ? rawValue
              : rawValue
              ? new Date(String(rawValue)).getTime()
              : NaN;
          if (!Number.isFinite(timestamp) || timestamp <= 0) {
            return 'N/A';
          }

          return new Date(timestamp).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          });
        },
      },
      {
        field: 'revisionCreator',
        headerName: 'Modified By',
        flex: 1,
        minWidth: 150,
        valueGetter: (_value, row) => row?.revisionCreator?.person,
        renderCell: (params: GridRenderCellParams) => {
          const value = String(params.value ?? '');
          const content = (
            <Typography
              component="div"
              variant="body2"
              noWrap
              sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value}
            </Typography>
          );

          return (
            <Stack
              flexDirection="row"
              alignItems="center"
              gap={2}
              sx={{ height: '100%' }}
            >
              <UserAvatar
                userEmail={params.value}
                sx={{ width: 32, height: 32 }}
              />
              {content}
            </Stack>
          );
        },
      },
      {
        field: 'enabledApps',
        headerName: 'Applications',
        width: 110,
        valueGetter: (_value, row) => {
          const appliedApps = row?.enabledAppSettings?.enabledApps ?? [];
          const appNames = appliedApps.map(
            (appObj: { app?: string | null }) => {
              return (
                String(appObj?.app ?? 'Unknown')
                  .charAt(0)
                  .toUpperCase() +
                String(appObj?.app ?? 'Unknown')
                  .slice(1)
                  .toLowerCase()
              );
            }
          );

          if (appNames.length === 0) {
            return 'None';
          }

          return appNames.join(', ');
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 190,
        type: 'singleSelect',
        valueOptions: [
          { value: 'published', label: 'Published' },
          { value: 'unpublished_draft', label: 'Draft' },
          { value: 'disabled', label: 'Disabled' },
        ],
        valueGetter: (_value, row) =>
          String(row?.lifecycle?.state ?? 'unknown').toLowerCase(),
        valueFormatter: (value, row) => {
          const val = String(value ?? 'unknown');
          const normalized = val.trim().toLowerCase();
          if (normalized === 'published') {
            return row?.lifecycle?.hasUnpublishedChanges
              ? 'Published, New Draft'
              : 'Published';
          }
          if (normalized === 'unpublished_draft') return 'Draft';
          if (normalized === 'disabled') return 'Disabled';
          return 'Unknown';
        },
        renderCell: (params: GridRenderCellParams<LabelWithRank>) => {
          const lifecycleStatus = getLabelLifecycleStatus(params.row);

          if (lifecycleStatus === 'PUBLISHED_NEW_DRAFT') {
            const successColor =
              theme.palette.mode === 'light'
                ? statusPalette.success.lightMode
                : statusPalette.success.darkMode;
            const infoColor =
              theme.palette.mode === 'light'
                ? statusPalette.info.lightMode
                : statusPalette.info.darkMode;

            return (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                height="100%"
              >
                <CircleIcon
                  sx={{
                    color: successColor,
                    fontSize: 12,
                    display: 'block',
                    transform: 'translateY(-1px)',
                  }}
                />
                <Typography
                  sx={{ fontWeight: 500, fontSize: 14, lineHeight: 1 }}
                >
                  <Box component="span" sx={{ color: successColor }}>
                    Published,{' '}
                  </Box>
                  <Box component="span" sx={{ color: infoColor }}>
                    New Draft
                  </Box>
                </Typography>
              </Stack>
            );
          }

          return <StatusLabel status={lifecycleStatus} />;
        },
      },
      ...(canManageLabels
        ? [
            {
              field: 'actions',
              headerName: '',
              width: 56,
              align: 'right' as const,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              disableExport: true,
              hideable: false,
              resizable: false,
              renderCell: (params: GridRenderCellParams<LabelWithRank>) => (
                <LabelActionsMenu
                  label={params.row}
                  hasActiveLock={!!hasActiveLockByLabelId[params.row.id ?? '']}
                  onPublish={requestPublish}
                  onClone={cloneLabel}
                  onDelete={(label) => requestDelete([label])}
                  onDisable={(label) => requestDisable([label])}
                  onEnable={(label) => requestEnable([label])}
                  isDeleting={isDeleting}
                  isPublishing={isPublishing}
                  onEdit={(label) => openEditLabel(label.id ?? '')}
                />
              ),
            },
          ]
        : []),
    ],
    [
      theme.palette.mode,
      hasActiveLockByLabelId,
      isLockPendingByLabelId,
      canManageLabels,
      requestDelete,
      requestDisable,
      requestEnable,
      openEditLabel,
      isDeleting,
      requestPublish,
      isPublishing,
      cloneLabel,
    ]
  );

  const { initialState } = useGridColumns(columns, 'labelManagerGridStates');

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    initialState?.filter?.filterModel || {
      items: [],
    }
  );

  useEffect(() => {
    if (initialState?.filter?.filterModel) {
      setFilterModel(initialState.filter.filterModel);
    }
  }, [initialState]);

  const selectedLabels = useMemo(
    () =>
      allLabels.filter((label) => label.id && selectedRows.includes(label.id)),
    [allLabels, selectedRows]
  );
  const ToolbarComponent = useToolbar(LabelManagerGridToolbar, {
    selectedLabels,
    hasActiveLockByLabelId,
    onDisable: () => requestDisable(selectedLabels),
    onDelete: () => requestDelete(selectedLabels),
    isDeleting,
    onEnable: () => requestEnable(selectedLabels),
  });
  const hasStatusFilter = filterModel.items.some(
    (item) => item.field === 'status'
  );
  const totalActive = !hasStatusFilter;
  const publishedActive = filterModel.items.some(
    (item) =>
      item.field === 'status' &&
      item.operator === 'is' &&
      item.value === 'published'
  );
  const draftActive = filterModel.items.some(
    (item) =>
      item.field === 'status' &&
      item.operator === 'is' &&
      item.value === 'unpublished_draft'
  );

  if (!driveLabelsAccessGranted && !checkingDriveLabelsAccess) {
    return <ScopesAlert apiKey="driveLabels" />;
  }

  return (
    <Stack direction="column" sx={{ height: '100%' }}>
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction="row"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2, width: '100%' }}
        >
          <StatCard
            color="slateBlue"
            label="Total Labels"
            value={allLabels.length}
            icon={<Label />}
            onClick={
              totalActive
                ? undefined
                : () =>
                    setFilterModel((prev) => ({
                      ...prev,
                      items: prev.items.filter(
                        (item) => item.field !== 'status'
                      ),
                    }))
            }
            active={totalActive}
            disabled={totalActive}
            sx={{
              flex: 1,
              minWidth: 240,
            }}
          />
          <StatCard
            color="teal"
            label="Published Labels"
            value={publishedCount}
            icon={<Check />}
            onClick={
              publishedActive
                ? undefined
                : () =>
                    setFilterModel((prev) => ({
                      ...prev,
                      items: [
                        ...prev.items.filter((item) => item.field !== 'status'),
                        {
                          field: 'status',
                          operator: 'is',
                          value: 'published',
                        },
                      ],
                    }))
            }
            active={publishedActive}
            disabled={publishedActive}
            sx={{
              flex: 1,
              minWidth: 240,
            }}
          />
          <StatCard
            color="cyan"
            label="Draft"
            value={draftCount}
            icon={<AccessTime />}
            onClick={
              draftActive
                ? undefined
                : () =>
                    setFilterModel((prev) => ({
                      ...prev,
                      items: [
                        ...prev.items.filter((item) => item.field !== 'status'),
                        {
                          field: 'status',
                          operator: 'is',
                          value: 'unpublished_draft',
                        },
                      ],
                    }))
            }
            active={draftActive}
            disabled={draftActive}
            sx={{
              flex: 1,
              minWidth: 240,
            }}
          />
        </Stack>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <SharedDataGridClient
          gridStateKey="labelManagerGridStates"
          gridKey={
            viewType.labelManagerGridStates?.activeGridState || 'default'
          }
          slots={{
            toolbar: ToolbarComponent,
            ...createGridOverlays('Labels', <AddToDrive />),
          }}
          defaultGridState={{
            columns: {
              columnVisibilityModel: {
                id: false,
              },
            },
            sorting: {
              sortModel: [
                {
                  field: 'rankOrder',
                  sort: 'asc',
                },
              ],
            },
          }}
          rows={allLabels}
          getRowId={(row) => row.id ?? ''}
          columns={columns}
          loading={
            labelsLoading || labelsFetching || isDeleting || isPublishing
          }
          checkboxSelection={canManageLabels}
          disableRowSelectionOnClick
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          filterMode="client"
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          onRowClick={(params: GridRowParams<LabelWithRank>) =>
            setSelectedLabelId(params.row.id ?? null)
          }
          sx={{
            height: '100%',
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Box>
      {labelActions.dialogs}
      <Drawer
        anchor="right"
        open={Boolean(selectedLabel)}
        onClose={() => setSelectedLabelId(null)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', md: '45%' },
          },
        }}
      >
        {selectedLabel && (
          <LabelDetailsDrawer
            selectedLabel={selectedLabel}
            onClose={() => setSelectedLabelId(null)}
            labelActions={canManageLabels ? labelActions : undefined}
            onEdit={(label) => openEditLabel(label.id ?? '')}
          />
        )}
      </Drawer>
    </Stack>
  );
}
